import { useState } from 'react';
import { sessionSpreads } from '../data';

const ratingConfig: Record<string, { bg: string; text: string; label: string; border: string; dot: string }> = {
  excellent: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Excellent', border: 'border-l-emerald-500', dot: 'bg-emerald-500' },
  good:      { bg: 'bg-green-100',   text: 'text-green-700',   label: 'Good',      border: 'border-l-green-400',   dot: 'bg-green-400' },
  fair:      { bg: 'bg-amber-100',   text: 'text-amber-700',   label: 'Fair',      border: 'border-l-amber-400',   dot: 'bg-amber-400' },
  poor:      { bg: 'bg-red-100',     text: 'text-red-600',     label: 'Poor',      border: 'border-l-red-400',     dot: 'bg-red-400' },
};

export default function SessionSpreadSection() {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const maxSpread = 3.0;
  const maxCost = 30;

  const handleToggle = (i: number) => {
    setExpandedIndex((prev) => (prev === i ? null : i));
  };

  return (
    <section id="session-spread" className="w-full bg-background-50 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="max-w-[720px] mb-10 md:mb-14">
          <h2 className="text-[32px] md:text-[42px] font-[750] leading-[1.15] text-foreground-950 tracking-tight mb-4">
            Spread tightens and widens
            <br />
            across the trading day
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            The same EURUSD pair can cost 0.5 pips at 10 AM and 2.0 pips at midnight. Click any time slot to see what is happening in the market and what it means for your trading costs.
          </p>
        </div>

        {/* Chart visualization */}
        <div className="bg-white rounded-card border border-background-200 p-4 md:p-8">

          {/* Legend — mobile 2×2 grid, desktop flex row */}
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-3 sm:gap-5 mb-6">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-primary-500 flex-shrink-0" />
              <span className="text-[13px] text-foreground-600">EURUSD</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-accent-500 flex-shrink-0" />
              <span className="text-[13px] text-foreground-600">GBPUSD</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-secondary-500 flex-shrink-0" />
              <span className="text-[13px] text-foreground-600">USDJPY</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-primary-100 border border-primary-300 flex-shrink-0" />
              <span className="text-[13px] text-foreground-600">Best window</span>
            </div>
          </div>

          {/* Timeline rows */}
          <div className="space-y-1.5">
            {sessionSpreads.map((slot, i) => {
              const isExpanded = expandedIndex === i;
              const isActive = slot.active;
              const eurusdPct = (slot.eurusd / maxSpread) * 100;
              const gbpusdPct = (slot.gbpusd / maxSpread) * 100;
              const usdjpyPct = (slot.usdjpy / maxSpread) * 100;
              const rating = ratingConfig[slot.rating];

              return (
                <div key={slot.hour} className="group">

                  {/* ── Desktop row (sm+): original 3-bar layout ──────── */}
                  <button
                    onClick={() => handleToggle(i)}
                    className={`hidden sm:flex w-full items-center gap-3 md:gap-4 py-2.5 px-3 rounded-[12px] transition-all duration-200 cursor-pointer text-left ${
                      isExpanded ? 'bg-background-100/80' : 'hover:bg-background-50'
                    }`}
                  >
                    {/* Time label */}
                    <div className="w-[90px] flex-shrink-0">
                      <div className={`text-[14px] font-bold whitespace-nowrap ${isActive ? 'text-primary-600' : 'text-foreground-700'}`}>
                        {slot.hour}
                      </div>
                      <div className={`text-[11px] whitespace-nowrap ${isActive ? 'text-primary-400' : 'text-foreground-400'}`}>
                        {slot.label}
                      </div>
                    </div>
                    {/* Active dot */}
                    <div className="flex-shrink-0">
                      {isActive
                        ? <span className="block w-[6px] h-[6px] rounded-full bg-primary-500" />
                        : <span className="block w-[6px] h-[6px] rounded-full bg-background-300" />}
                    </div>
                    {/* Bars */}
                    <div className="flex-1 space-y-1.5 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-[7px] bg-background-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-primary-500 transition-all duration-500" style={{ width: `${eurusdPct}%` }} />
                        </div>
                        <span className={`text-[12px] font-semibold w-[36px] text-right ${isExpanded ? 'text-primary-700' : 'text-foreground-500'}`}>{slot.eurusd}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-[7px] bg-background-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-accent-500 transition-all duration-500" style={{ width: `${gbpusdPct}%` }} />
                        </div>
                        <span className={`text-[12px] font-semibold w-[36px] text-right ${isExpanded ? 'text-accent-700' : 'text-foreground-500'}`}>{slot.gbpusd}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-[7px] bg-background-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-secondary-500 transition-all duration-500" style={{ width: `${usdjpyPct}%` }} />
                        </div>
                        <span className={`text-[12px] font-semibold w-[36px] text-right ${isExpanded ? 'text-secondary-700' : 'text-foreground-500'}`}>{slot.usdjpy}</span>
                      </div>
                    </div>
                    {/* Chevron */}
                    <div className={`flex-shrink-0 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-[18px] text-foreground-400 w-5 h-5 flex items-center justify-center" />
                    </div>
                  </button>

                  {/* ── Mobile row (< sm): compact card-style ─────────── */}
                  <button
                    onClick={() => handleToggle(i)}
                    className={`sm:hidden w-full flex items-stretch gap-0 rounded-[12px] overflow-hidden border transition-all duration-200 cursor-pointer text-left ${
                      isExpanded
                        ? 'border-background-300 bg-background-50'
                        : isActive
                        ? 'border-primary-200 bg-primary-50/40'
                        : 'border-background-200 bg-white'
                    }`}
                  >
                    {/* Left rating color strip */}
                    <div className={`w-[4px] flex-shrink-0 ${rating.dot} opacity-80`} />

                    {/* Time + label */}
                    <div className="flex-shrink-0 w-[68px] px-3 py-3 flex flex-col justify-center">
                      <div className={`text-[14px] font-bold leading-tight ${isActive ? 'text-primary-600' : 'text-foreground-800'}`}>
                        {slot.hour}
                      </div>
                      <div className={`text-[10px] leading-tight mt-0.5 ${isActive ? 'text-primary-400' : 'text-foreground-400'}`}>
                        {slot.label}
                      </div>
                    </div>

                    {/* Mini EURUSD bar — grows to fill */}
                    <div className="flex-1 flex flex-col justify-center px-2 py-3 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-[10px] text-foreground-400 whitespace-nowrap">EUR</span>
                        <div className="flex-1 h-[5px] bg-background-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-primary-500 transition-all duration-500"
                            style={{ width: `${eurusdPct}%` }}
                          />
                        </div>
                        <span className={`text-[11px] font-bold w-[24px] text-right ${isActive ? 'text-primary-600' : 'text-foreground-600'}`}>
                          {slot.eurusd}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-foreground-400 whitespace-nowrap">JPY</span>
                        <div className="flex-1 h-[5px] bg-background-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-secondary-500 transition-all duration-500"
                            style={{ width: `${usdjpyPct}%` }}
                          />
                        </div>
                        <span className="text-[11px] font-bold w-[24px] text-right text-foreground-500">
                          {slot.usdjpy}
                        </span>
                      </div>
                    </div>

                    {/* Right: rating badge + chevron */}
                    <div className="flex-shrink-0 flex flex-col items-end justify-center gap-1.5 pr-3 pl-1 py-3">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${rating.bg} ${rating.text}`}>
                        {rating.label}
                      </span>
                      <div className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                        <i className="ri-arrow-down-s-line text-[16px] text-foreground-400 w-4 h-4 flex items-center justify-center" />
                      </div>
                    </div>
                  </button>

                  {/* ── Expanded detail panel (shared mobile + desktop) ─ */}
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isExpanded ? 'max-h-[700px] opacity-100 mt-1' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-3 pb-4 pt-3 mx-2 sm:mx-3 rounded-b-[12px] bg-background-50/80 border-t border-background-200">
                      {/* Session badge + rating */}
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background-100 text-[11px] font-semibold text-foreground-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-foreground-400" />
                          {slot.session}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${rating.bg} ${rating.text}`}>
                          {rating.label}
                        </span>
                      </div>

                      {/* Markets & description */}
                      <div className="mb-3">
                        <div className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wide mb-1">Active Markets</div>
                        <div className="text-[13px] text-foreground-700 leading-relaxed mb-1.5">{slot.markets}</div>
                        <div className="text-[12px] text-foreground-500 leading-relaxed">{slot.detail}</div>
                      </div>

                      {/* Cost breakdown */}
                      <div className="mb-3">
                        <div className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wide mb-2">Cost · 1 Standard Lot</div>

                        {/* Mobile: 3-col pill grid */}
                        <div className="grid grid-cols-3 gap-2 sm:hidden">
                          {[
                            { pair: 'EURUSD', cost: slot.eurusdCost, color: 'bg-primary-50 border-primary-200', text: 'text-primary-700', dot: 'bg-primary-500' },
                            { pair: 'GBPUSD', cost: slot.gbpusdCost, color: 'bg-accent-50 border-accent-200', text: 'text-accent-700', dot: 'bg-accent-500' },
                            { pair: 'USDJPY', cost: slot.usdjpyCost, color: 'bg-secondary-50 border-secondary-200', text: 'text-secondary-700', dot: 'bg-secondary-500' },
                          ].map(({ pair, cost, color, text, dot }) => (
                            <div key={pair} className={`rounded-xl border px-2 py-2.5 text-center ${color}`}>
                              <div className="flex items-center justify-center gap-1 mb-1">
                                <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                                <span className="text-[10px] font-semibold text-foreground-500">{pair}</span>
                              </div>
                              <div className={`text-[16px] font-[800] leading-none ${text}`}>${cost.toFixed(0)}</div>
                              <div className="text-[9px] text-foreground-400 mt-0.5">per lot</div>
                            </div>
                          ))}
                        </div>

                        {/* Desktop: original bar layout */}
                        <div className="hidden sm:block space-y-2.5">
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[13px] font-medium text-foreground-700">EURUSD</span>
                              <span className="text-[13px] font-semibold text-primary-600">${slot.eurusdCost.toFixed(2)}</span>
                            </div>
                            <div className="h-[5px] bg-background-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-primary-500 transition-all duration-500" style={{ width: `${(slot.eurusdCost / maxCost) * 100}%` }} />
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[13px] font-medium text-foreground-700">GBPUSD</span>
                              <span className="text-[13px] font-semibold text-accent-600">${slot.gbpusdCost.toFixed(2)}</span>
                            </div>
                            <div className="h-[5px] bg-background-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-accent-500 transition-all duration-500" style={{ width: `${(slot.gbpusdCost / maxCost) * 100}%` }} />
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[13px] font-medium text-foreground-700">USDJPY</span>
                              <span className="text-[13px] font-semibold text-secondary-600">${slot.usdjpyCost.toFixed(2)}</span>
                            </div>
                            <div className="h-[5px] bg-background-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-secondary-500 transition-all duration-500" style={{ width: `${(slot.usdjpyCost / maxCost) * 100}%` }} />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Recommendation */}
                      <div className="flex items-start gap-2 p-3 rounded-[10px] bg-white border border-background-200">
                        <div className="w-5 h-5 rounded-md bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-lightbulb-line text-[12px] text-accent-600 w-3 h-3 flex items-center justify-center" />
                        </div>
                        <p className="text-[12px] text-foreground-700 leading-relaxed">{slot.recommendation}</p>
                      </div>
                    </div>
                  </div>

                </div>
              );
            })}
          </div>

          {/* Bottom insight */}
          <div className="mt-6 pt-5 border-t border-background-200 flex items-start gap-3">
            <div className="w-8 h-8 rounded-[10px] bg-primary-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-[16px] text-primary-600 w-5 h-5 flex items-center justify-center" />
            </div>
            <div>
              <div className="text-[14px] font-semibold text-foreground-800 mb-0.5">Sweet spot: 10:00–14:00 UTC</div>
              <div className="text-[13px] text-foreground-600 leading-relaxed">
                London and New York overlap creates the deepest liquidity pool. EURUSD spreads often drop below 0.5 pips during this window. If you trade outside this window, expect to pay 2–4x more in spread.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}